/**
 * Access tokens for the parts of this app that are not components:
 * `lib/api.js`, `context/AxiosContext.tsx`, the RTK Query base query in
 * `stores/apiSlice.js`, `Navbar.jsx`, and the redux store bootstrap in
 * `stores/store.js`.
 *
 * Before the Cognito migration these all read `localStorage['access-token']`
 * synchronously. The current token now belongs to the shared oidc-client-ts
 * `UserManager` (`auth/cognito.js`), which this module asks directly. It
 * never mirrors the token into localStorage, which keeps the decision in
 * `frontend/` to keep tokens out of persistent, origin-shared storage.
 *
 * This module never navigates. Starting sign-in belongs to the route guards
 * (`CognitoSessionGuard` and `/sign-in`). When a token getter could start an
 * authorize redirect or a hidden `prompt=none` iframe, any API call in flight
 * could do it, including the ones still running on `/sign-out`. Cognito's
 * live SSO cookie answered those silently and signed the user straight back
 * in.
 */
import { getOidcUserStorageKey, getUserManager } from './cognito';

/**
 * Reads the OIDC user that oidc-client-ts persisted in sessionStorage.
 *
 * This reaches into another library's storage on purpose. It is the only way
 * to answer "who is signed in" synchronously, which `stores/store.js` needs at
 * module-evaluation time to pick its persisted-state key, long before any
 * React provider has mounted. Treated as read-only and entirely best-effort:
 * any parse failure resolves to "no session" rather than throwing.
 */
export function readStoredOidcUser() {
  try {
    const raw = window.sessionStorage.getItem(getOidcUserStorageKey());

    if (!raw) {
      return null;
    }

    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * The Cognito `sub` of the signed-in user, read synchronously, or '' when
 * there is no usable session. Replaces the previous `jwtDecode(accessToken).sub`.
 *
 * Deliberately reads the ID token claims that oidc-client-ts already parsed and
 * stored, instead of decoding the access token: decoding a token to read claims
 * is not verification, and the profile is right there having been validated
 * during the code exchange.
 */
export function getIdentitySub() {
  const storedUser = readStoredOidcUser();

  return (storedUser && storedUser.profile && storedUser.profile.sub) || '';
}

/**
 * One refresh at a time. Every request that needs a token while it is expired
 * waits for the same refresh instead of each redeeming the refresh token.
 */
let pendingRefresh = null;

function refreshOnce(userManager) {
  if (!pendingRefresh) {
    pendingRefresh = userManager.signinSilent().finally(() => {
      pendingRefresh = null;
    });
  }

  return pendingRefresh;
}

/**
 * Current Cognito access token, or `null` when there is no usable session.
 *
 * - No stored user: `null`. Callers keep their existing "not logged in"
 *   branches and the guard starts sign-in.
 * - Expired with a refresh token: refreshed through the token endpoint. A
 *   reload made an hour later stays signed in without an authorize round trip.
 * - Refresh failed: the session is dead, so it is removed. That flips the
 *   provider to unauthenticated and `CognitoSessionGuard` starts sign-in, back
 *   to the current page.
 *
 * `signinSilent` is only ever called when a refresh token exists, so it always
 * uses the refresh grant and never the hidden `prompt=none` iframe.
 */
export async function getAccessToken() {
  const userManager = getUserManager();
  const user = await userManager.getUser();

  if (!user) {
    return null;
  }

  if (!user.expired) {
    return user.access_token;
  }

  if (user.refresh_token) {
    try {
      const renewed = await refreshOnce(userManager);

      if (renewed && !renewed.expired) {
        return renewed.access_token;
      }
    } catch {
      // Handled below.
    }
  }

  try {
    await userManager.removeUser();
  } catch {
    // Best effort. The caller still gets "no token".
  }

  return null;
}
